import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  IconButton,
  Link as MuiLink,
} from "@mui/material";
import {
  LinkedIn,
  Twitter,
  Email,
  ArrowForward,
} from "@mui/icons-material";
import { styled, keyframes } from "@mui/material/styles";

import logo from "../../images/logo3.png";

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Styled Components
const FooterContainer = styled(Box)({
  position: "relative",
  background: "linear-gradient(180deg, #000000 0%, #000000 100%)",
  overflow: "hidden",
  paddingTop: "80px",
  paddingBottom: "40px",
  borderTop: "1px solid rgba(193, 227, 40, 0.3)",
});

const GradientOrb = styled(Box)(({ top, left, bottom, right, color }) => ({
  position: "absolute",
  width: 400,
  height: 400,
  borderRadius: "50%",
  background: color,
  filter: "blur(100px)",
  pointerEvents: "none",
  top,
  left,
  bottom,
  right,
}));

const GridPattern = styled(Box)({
  position: "absolute",
  inset: 0,
  opacity: 0.03,
  backgroundImage: `linear-gradient(rgba(148, 163, 184, 0.1) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(148, 163, 184, 0.1) 1px, transparent 1px)`,
  backgroundSize: "50px 50px",
  pointerEvents: "none",
});

const LogoImage = styled("img")({
  height: 80,
  width: "auto",
  marginBottom:5,
  filter: "brightness(1.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    filter: "brightness(1.3) drop-shadow(0 0 20px rgba(193, 227, 40, 0.5))",
    transform: "scale(1.05)",
  },
});

const SocialIconButton = styled(IconButton)({
  width: 44,
  height: 44,
  background: "rgba(0, 0, 0, 0.8)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(193, 227, 40, 0.3)",
  transition: "all 0.3s ease",
  color: "rgba(148, 163, 184, 1)",
  "&:hover": {
    background: "linear-gradient(135deg, #c1e328 0%, #9fcc2e 100%)",
    borderColor: "transparent",
    transform: "translateY(-4px)",
    boxShadow: "0 10px 25px rgba(193, 227, 40, 0.4)",
    color: "#000",
  },
});

const FooterLink = styled("a")({
  color: "rgba(148, 163, 184, 1)",
  textDecoration: "none",
  fontSize: "0.875rem",
  transition: "all 0.3s ease",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 0",
  position: "relative",
  cursor: "pointer",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    width: 0,
    height: 2,
    bottom: 0,
    background: "linear-gradient(90deg, #c1e328, #9fcc2e)",
    transition: "width 0.3s ease",
  },
  "&:hover": {
    color: "#c1e328",
    paddingLeft: "8px",
    "&::before": {
      width: "30px",
    },
  },
});

const SectionTitle = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 700,
  letterSpacing: 2,
  color: "#c1e328",
  marginBottom: 20,
  textTransform: "uppercase",
  position: "relative",
  display: "inline-block",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -8,
    left: 0,
    width: 40,
    height: 2,
    background: "linear-gradient(90deg, #c1e328, transparent)",
  },
});

const EmailLink = styled(MuiLink)({
  color: "rgba(203, 213, 225, 1)",
  textDecoration: "none",
  fontSize: "1rem",
  fontWeight: 500,
  transition: "all 0.3s ease",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 12px",
  borderRadius: 8,
  background: "rgba(0, 0, 0, 0.5)",
  border: "1px solid rgba(193, 227, 40, 0.3)",
  "&:hover": {
    color: "#c1e328",
    borderColor: "rgba(193, 227, 40, 0.5)",
    background: "rgba(193, 227, 40, 0.1)",
    transform: "translateX(4px)",
  },
});

const CopyrightBox = styled(Box)({
  marginTop: 60,
  paddingTop: 30,
  borderTop: "1px solid rgba(193, 227, 40, 0.2)",
  textAlign: "center",
});

const FooterNew = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: "Home", href: "/" },
    { label: "Agri Robotics", href: "/agri-robots" },
    { label: "About Us", href: "/about" },
    { label: "Contact Us", href: "/contact" },
    { label: "Other Usecase", href: "/service-robots" },
  ];

  const socialLinks = [
    {
      icon: LinkedIn,
      url: "https://www.linkedin.com/company/autogenbot",
      label: "LinkedIn",
    },
    {
      icon: Twitter,
      url: "https://twitter.com/Autogenbot",
      label: "Twitter",
    },
    {
      icon: Email,
      url: "mailto:aircorpofficial@gmail.com",
      label: "Email",
    },
  ];

  return (
    <FooterContainer>
      {/* Background Elements */}
      <GradientOrb top="20%" left={-200} color="rgba(193, 227, 40, 0.08)" />
      <GradientOrb bottom="-10%" right={-200} color="rgba(193, 227, 40, 0.08)" />
      <GridPattern />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={6}>
          {/* Logo and Social Section */}
          <Grid item xs={12} md={4}>
            <Box>
              <LogoImage src={logo} alt="Company Logo" />
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(148, 163, 184, 1)",
                  mb: 3,
                  lineHeight: 1.8,
                  maxWidth: 300,
                }}
              >
             Building the future of Physical AI for offroad robotic mobility
              </Typography>

              {/* Social Icons */}
              <Stack direction="row" spacing={2}>
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <SocialIconButton
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                    >
                      <Icon sx={{ fontSize: 20 }} />
                    </SocialIconButton>
                  );
                })}
              </Stack>
            </Box>
          </Grid>

          {/* Quick Links Section */}
          <Grid item xs={12} sm={6} md={3}>
            <SectionTitle variant="h6">Quick Links</SectionTitle>
            <Stack spacing={0.5}>
              {quickLinks.map((link, index) => (
                <FooterLink key={index} href={link.href}>
                  <ArrowForward sx={{ fontSize: 14 }} />
                  {link.label}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          {/* Reach Us Section */}
          <Grid item xs={12} sm={6} md={5}>
            <SectionTitle variant="h6">Reach Us</SectionTitle>
            <Typography
                variant="body2"
                sx={{
                  color: "rgba(148, 163, 184, 1)",
                  mb: 2,
                  lineHeight: 1.8,
                }}
              >
                Get in touch with us for enquiries, partnerships, or to learn
                more about our innovative solutions.
              </Typography>
            <EmailLink
              href="mailto:aircorpofficial@gmail.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Email sx={{ fontSize: 18 }} />
              aircorpofficial@gmail.com
            </EmailLink>
          </Grid>
        </Grid>

        {/* Divider and Copyright */}
        <CopyrightBox>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Typography
              variant="body2"
              sx={{ color: "rgba(148, 163, 184, 1)", fontSize: "0.875rem" }}
            >
              © {currentYear} AutogenBot. All rights reserved.
            </Typography>
            
            <Stack direction="row" spacing={3}>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(148, 163, 184, 1)",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  transition: "color 0.3s ease",
                  "&:hover": {
                    color: "#c1e328",
                  },
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(148, 163, 184, 1)",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  transition: "color 0.3s ease",
                  "&:hover": {
                    color: "#c1e328",
                  },
                }}
              >
                Terms of Service
              </Typography>
            </Stack>
          </Stack>

          {/* Made with love indicator */}
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(148, 163, 184, 0.6)",
                fontSize: "0.75rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              Made with
              <Box
                component="span"
                sx={{
                  color: "#c1e328",
                  animation: `${pulse} 2s ease-in-out infinite`,
                  fontSize: "1rem",
                }}
              >
                ♥
              </Box>
              in India
            </Typography>
          </Box>
        </CopyrightBox>
      </Container>
    </FooterContainer>
  );
};

export default FooterNew;

